import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import { idempotencyKeyFor } from '@/lib/http-core';

/**
 * QA-292 — 주문 관리자 메모(여러 건, 고도몰 es_adminOrderGoodsMemo 와 같은 기능). be-order PR #44.
 *   GET    /api/admin/v1/orders/{orderId}/admin-memos            → data.items[] (작성일 오래된 순, 삭제분 제외)
 *   POST   /api/admin/v1/orders/{orderId}/admin-memos            { scope, orderItemId?, category?, content } → 201 메모
 *   PATCH  /api/admin/v1/orders/{orderId}/admin-memos/{memoId}   { category?, content } → 메모 (category 생략/null = 구분 없음)
 *   DELETE /api/admin/v1/orders/{orderId}/admin-memos/{memoId}   → 204 (삭제 표시만)
 * 수정·삭제는 작성자 본인 또는 SUPER_ADMIN 만(BE 403 ORDER_ACCESS_FORBIDDEN).
 * 주문 상세의 adminMemo·memoEntries(한 줄 메모·고객 요청·상담 이력)와는 별개다.
 */
export type AdminMemoScope = 'ORDER' | 'ITEM';

/** 고도몰 메모 구분 코드 순서 그대로. */
export const ADMIN_MEMO_CATEGORIES = [
  'RECEIVED',
  'DONE',
  'DEPOSIT',
  'SHIPPING',
  'SOLD_OUT',
  'EXCHANGE',
  'RETURN',
  'REFUND',
  'AS',
  'ETC',
] as const;
export type AdminMemoCategory = (typeof ADMIN_MEMO_CATEGORIES)[number];

export const ADMIN_MEMO_MAX_LENGTH = 2000;

export interface OrderAdminMemo {
  memoId: string;
  scope: AdminMemoScope;
  orderItemId: string | null;
  category: AdminMemoCategory | null;
  content: string;
  writtenAt: string;
  writtenBy: string | null;
  /** 작성자 관리자 ID. 이관 메모는 "godo:관리자번호". 수정·삭제 버튼 노출 판단용. */
  writtenById: string | null;
  updatedAt: string | null;
}

export interface CreateAdminMemoBody {
  scope: AdminMemoScope;
  orderItemId?: string;
  category?: AdminMemoCategory | null;
  content: string;
}

export interface UpdateAdminMemoBody {
  category: AdminMemoCategory | null;
  content: string;
}

interface Envelope<T> {
  data?: T | null;
}

const base = (orderId: string) => `/api/admin/v1/orders/${encodeURIComponent(orderId)}/admin-memos`;

export const orderAdminMemosQueryKey = (orderId: string) => ['admin', 'order-admin-memos', orderId] as const;

export const useOrderAdminMemosQuery = (orderId: string) =>
  useQuery<OrderAdminMemo[]>({
    queryKey: orderAdminMemosQueryKey(orderId),
    enabled: !!orderId,
    queryFn: async () => {
      const res = await adminFetch(base(orderId));
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as Envelope<{ items?: OrderAdminMemo[] }>;
      return env.data?.items ?? [];
    },
  });

export const useOrderAdminMemoMutations = (orderId: string) => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: orderAdminMemosQueryKey(orderId) });

  const create = useMutation({
    mutationFn: async (body: CreateAdminMemoBody): Promise<OrderAdminMemo> => {
      const path = base(orderId);
      const res = await adminFetch(path, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', path, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw await errorFromResponse(res);
      return ((await res.json()) as Envelope<OrderAdminMemo>).data as OrderAdminMemo;
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ memoId, body }: { memoId: string; body: UpdateAdminMemoBody }): Promise<OrderAdminMemo> => {
      const res = await adminFetch(`${base(orderId)}/${encodeURIComponent(memoId)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw await errorFromResponse(res);
      return ((await res.json()) as Envelope<OrderAdminMemo>).data as OrderAdminMemo;
    },
    onSettled: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (memoId: string): Promise<void> => {
      const res = await adminFetch(`${base(orderId)}/${encodeURIComponent(memoId)}`, { method: 'DELETE' });
      if (!res.ok) throw await errorFromResponse(res);
    },
    onSettled: invalidate,
  });

  return { create, update, remove };
};
